// Plays an interviewer line: server voice (OpenAI) first, the browser's speech synthesis as a fallback.
// Browsers only play sound after a click on the page, so call unlockAudio() inside the Start click.
import { pickBrowserVoice, pitchFor } from './voicePrefs';

let sharedPlayer = null;
let sharedContext = null;

const silentWav = () => {
  const bytes = [
    ...'RIFF', 37, 0, 0, 0, ...'WAVE', ...'fmt ', 16, 0, 0, 0, 1, 0, 1, 0,
    64, 31, 0, 0, 64, 31, 0, 0, 1, 0, 8, 0, ...'data', 1, 0, 0, 0, 128,
  ].map((b) => (typeof b === 'string' ? b.charCodeAt(0) : b));
  return `data:audio/wav;base64,${btoa(String.fromCharCode(...bytes))}`;
};

const isBlocked = (err) => err?.name === 'NotAllowedError';

/** Call synchronously from a click/tap handler: lets later lines play without another gesture. */
export const unlockAudio = () => {
  if (typeof window === 'undefined') return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      sharedContext = sharedContext || new Ctx();
      if (sharedContext.state === 'suspended') sharedContext.resume?.().catch(() => {});
    }
  } catch {
    /* no Web Audio */
  }
  try {
    if (typeof window.Audio === 'function') {
      sharedPlayer = sharedPlayer || new window.Audio();
      sharedPlayer.src = silentWav();
      const started = sharedPlayer.play?.();
      if (started && typeof started.catch === 'function') started.catch(() => {});
    }
  } catch {
    /* no audio element */
  }
  try {
    const synth = window.speechSynthesis;
    if (synth && typeof window.SpeechSynthesisUtterance !== 'undefined' && !synth.speaking) {
      const warm = new window.SpeechSynthesisUtterance(' ');
      warm.volume = 0;
      synth.speak(warm);
    }
  } catch {
    /* no speech synthesis */
  }
};

export const resetAudioUnlockForTests = () => {
  sharedPlayer = null;
  sharedContext = null;
};

const loadVoices = (synth, waitMs = 1000) =>
  new Promise((resolve) => {
    const now = synth.getVoices ? synth.getVoices() : [];
    if (now.length || typeof synth.addEventListener !== 'function') {
      resolve(now);
      return;
    }
    const done = () => {
      clearTimeout(timer);
      synth.removeEventListener?.('voiceschanged', done);
      resolve(synth.getVoices ? synth.getVoices() : []);
    };
    const timer = setTimeout(done, waitMs);
    synth.addEventListener('voiceschanged', done);
  });

/** Upper bound for one line: Chrome sometimes never fires onend (long lines, network voices). */
export const speechTimeoutMs = (text) => 6000 + String(text || '').length * 90;

// Resolves 'spoken', 'blocked' (no click on the page yet) or 'unavailable'.
const browserSpeak = (text, isCancelled, gender) =>
  new Promise((resolve) => {
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
    if (!synth || typeof window.SpeechSynthesisUtterance === 'undefined' || isCancelled()) {
      resolve('unavailable');
      return;
    }
    loadVoices(synth).then((voices) => {
      if (isCancelled()) {
        resolve('unavailable');
        return;
      }
      // Local voices keep speaking past ~15 s; Chrome's network voices can stop mid-line.
      const local = voices.filter((v) => v.localService);
      const voice = pickBrowserVoice(local, gender) || pickBrowserVoice(voices, gender);
      const utterance = new window.SpeechSynthesisUtterance(text);
      if (voice) utterance.voice = voice;
      utterance.pitch = pitchFor(gender, voice);
      utterance.rate = 1;
      let settled = false;
      const finish = (outcome) => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        resolve(outcome);
      };
      const watchdog = setTimeout(() => {
        synth.cancel();
        finish('spoken');
      }, speechTimeoutMs(text));
      utterance.onend = () => finish('spoken');
      utterance.onerror = (event) => finish(event?.error === 'not-allowed' ? 'blocked' : 'unavailable');
      synth.cancel();
      synth.resume?.();
      synth.speak(utterance);
    });
  });

// speakLine({text, fetchAudio, useServer, gender}) -> {done: Promise<{server, fetchFailed, blocked}>, cancel()}
// fetchFailed: the server had no voice. blocked: the browser refused to play sound until the user clicks.
export const speakLine = ({ text, fetchAudio, useServer = true, gender = 'female' }) => {
  let cancelled = false;
  let audio = null;
  let url = '';
  let fetchFailed = false;
  let playBlocked = false;
  const isCancelled = () => cancelled;

  const playServer = async () => {
    let blob;
    try {
      blob = await fetchAudio();
    } catch (err) {
      fetchFailed = true;
      throw err;
    }
    if (cancelled) return;
    url = URL.createObjectURL(blob);
    audio = sharedPlayer || new Audio();
    audio.src = url;
    await new Promise((resolve, reject) => {
      audio.onended = resolve;
      audio.onerror = reject;
      const started = audio.play();
      if (started && typeof started.catch === 'function') {
        started.catch((err) => {
          if (isBlocked(err)) playBlocked = true;
          reject(err);
        });
      }
    });
  };

  const done = (async () => {
    if (useServer && fetchAudio) {
      try {
        await playServer();
        return { server: true, fetchFailed: false, blocked: false };
      } catch {
        /* fall back to the browser voice */
      } finally {
        if (url) URL.revokeObjectURL(url);
      }
    }
    const outcome = await browserSpeak(text, isCancelled, gender);
    return {
      server: false,
      fetchFailed,
      blocked: !cancelled && (outcome === 'blocked' || (playBlocked && outcome !== 'spoken')),
    };
  })();

  return {
    done,
    cancel() {
      cancelled = true;
      if (audio) {
        audio.pause();
        audio.onended?.();
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
    },
  };
};
